import { useEffect, useState } from "react";
import { Plus, Briefcase, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { isManager } from "@/lib/roles";
import { Card, Btn, Loader } from "@/components/portal/ui";
import { StatusBadge } from "@/components/portal/StatusBadge";

// Optional jobs module — provider/manager view: list + create + admin approve.
export function JobsManager() {
  const { portalKey, categories, config } = usePortal();
  const { user } = useAuth();
  const { L } = useI18n();
  const manager = isManager(user, portalKey);
  const empTypes = config?.jobs?.employment_types || [];
  const [jobs, setJobs] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", category_id: "", employment_type: empTypes[0]?.key || "full_time", description: "", status: "pending" });

  const load = () => api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [portalKey]);

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  const fieldCls = "w-full px-3.5 h-11 text-sm border outline-none";

  const create = async () => {
    if (!form.title.trim()) return toast.error(L({ de: "Titel fehlt", fr: "Titre manquant", it: "Titolo mancante", en: "Title required" }));
    setSaving(true);
    try {
      await api.post("/jobs", form);
      toast.success(L({ de: "Stelle erstellt", fr: "Poste créé", it: "Posizione creata", en: "Job created" }));
      setCreating(false);
      setForm({ title: "", category_id: "", employment_type: empTypes[0]?.key || "full_time", description: "", status: "pending" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const approve = async (id) => {
    try { await api.post(`/admin/jobs/${id}/approve`); toast.success(L({ de: "Freigegeben", fr: "Validé", it: "Approvato", en: "Approved" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!jobs) return <Loader />;
  return (
    <div className="space-y-4" data-testid="jobs-manager">
      <div className="flex justify-end">
        <Btn onClick={() => setCreating((v) => !v)} data-testid="job-create-toggle"><Plus className="h-4 w-4" /> {L({ de: "Stelle erstellen", fr: "Créer un poste", it: "Crea posizione", en: "Create job" })}</Btn>
      </div>
      {creating && (
        <Card className="p-5 space-y-3" data-testid="job-create-form">
          <input data-testid="job-title" className={fieldCls} style={st} placeholder={L({ de: "Titel", fr: "Titre", it: "Titolo", en: "Title" })} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <select data-testid="job-category" className={fieldCls} style={st} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">{L({ de: "Kategorie", fr: "Catégorie", it: "Categoria", en: "Category" })}</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{L(c.name)}</option>)}
            </select>
            <select data-testid="job-emptype" className={fieldCls} style={st} value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
              {empTypes.map((et) => <option key={et.key} value={et.key}>{L(et.label)}</option>)}
            </select>
          </div>
          <textarea data-testid="job-desc" rows={3} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st} placeholder={L({ de: "Beschreibung", fr: "Description", it: "Descrizione", en: "Description" })} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Btn onClick={create} disabled={saving} data-testid="job-save" className="w-full">{L({ de: "Speichern", fr: "Enregistrer", it: "Salva", en: "Save" })}</Btn>
        </Card>
      )}
      {jobs.length === 0 ? (
        <Card className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{L({ de: "Noch keine Stellen.", fr: "Aucun poste.", it: "Nessuna posizione.", en: "No jobs yet." })}</Card>
      ) : jobs.map((j) => (
        <Card key={j.id} className="p-4 flex items-center gap-3" data-testid={`job-row-${j.id}`}>
          <Briefcase className="h-5 w-5 shrink-0" style={{ color: "var(--accent)" }} />
          <div className="min-w-0"><div className="font-semibold truncate" style={{ color: "var(--text)" }}>{j.title}</div><div className="text-xs" style={{ color: "var(--text-muted)" }}>{j.provider_name}</div></div>
          <div className="ml-auto flex items-center gap-2">
            <StatusBadge status={j.status} />
            {manager && !j.approved && <Btn variant="soft" onClick={() => approve(j.id)} data-testid={`job-approve-${j.id}`}><Check className="h-4 w-4" /> {L({ de: "Freigeben", fr: "Valider", it: "Approva", en: "Approve" })}</Btn>}
          </div>
        </Card>
      ))}
    </div>
  );
}
